import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  Building2, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  RefreshCw,
  DollarSign,
  Target,
  Zap,
  Crown,
  Briefcase,
  Plus,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Award,
  Activity
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Skeleton component
const SkeletonCard = () => (
  <div className="bg-gray-800 border-gray-700 rounded-lg p-4 animate-pulse">
    <div className="h-4 bg-gray-700 rounded w-1/3 mb-2" />
    <div className="h-6 bg-gray-700 rounded w-1/2" />
  </div>
);

export default function SmartMoney() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("alerts");
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [tradeForm, setTradeForm] = useState({
    shares: "",
    entry_price: ""
  });
  
  // Autopilot state
  const [autopilot, setAutopilot] = useState(null);
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [showPerformance, setShowPerformance] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, autopilotRes, performanceRes] = await Promise.all([
        api.get("/smart-money/overview"),
        api.get("/autopilot/status").catch(() => ({ data: null })),
        api.get("/autopilot/performance").catch(() => ({ data: null }))
      ]);
      setData(overviewRes.data);
      setAutopilot(autopilotRes.data);
      setPerformanceData(performanceRes.data);
    } catch (error) {
      console.error("Error fetching smart money data:", error);
      toast.error("Failed to load smart money data");
    }
    setLoading(false);
  };
  
  const toggleAutopilot = async () => {
    setAutopilotLoading(true);
    try {
      if (autopilot?.settings?.enabled) {
        await api.post("/autopilot/disable");
        toast.success("Autopilot disabled");
      } else {
        await api.post("/autopilot/enable", {
          track_politicians: true,
          track_insiders: true,
          track_fund_managers: true
        });
        toast.success("Autopilot enabled! Syncing trades...");
        // Auto-sync after enabling
        await syncAutopilot();
      }
      await fetchData();
    } catch (error) {
      toast.error("Failed to toggle autopilot");
    }
    setAutopilotLoading(false);
  };
  
  const syncAutopilot = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/autopilot/sync");
      const count = response.data?.new_trades?.length || 0;
      if (count > 0) {
        toast.success(`Synced ${count} new trades!`);
      } else {
        toast.info("No new trades to sync");
      }
      await fetchData();
    } catch (error) {
      toast.error("Failed to sync trades");
    }
    setSyncing(false);
  };
  
  const closeAutopilotTrade = async (symbol) => {
    try {
      const response = await api.post(`/autopilot/close/${symbol}`);
      toast.success(`Closed ${symbol}: ${response.data.pnl_pct > 0 ? '+' : ''}${response.data.pnl_pct}%`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to close trade");
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000); // Refresh every 2 min
    return () => clearInterval(interval);
  }, []);

  const handleTakeTrade = async () => {
    if (!tradeForm.shares || !tradeForm.entry_price) {
      toast.error("Please fill in shares and entry price");
      return;
    }

    try {
      const tradeData = {
        symbol: selectedAlert.symbol,
        trade_type: selectedAlert.action === "BUY" || selectedAlert.action?.includes("Purchase") ? "BUY" : "SELL",
        shares: parseFloat(tradeForm.shares),
        entry_price: parseFloat(tradeForm.entry_price),
        suggestion_source: selectedAlert.type,
        suggestion_type: `${selectedAlert.source} - ${selectedAlert.reason}`,
        notes: `Smart Money Alert: ${selectedAlert.reason}`
      };

      await api.post("/trade-tracker/add", tradeData);
      toast.success(`Trade logged: ${tradeData.trade_type} ${tradeData.symbol}`);
      setShowTradeDialog(false);
      setSelectedAlert(null);
      setTradeForm({ shares: "", entry_price: "" });
    } catch (error) {
      toast.error("Failed to log trade");
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-6" data-testid="smart-money-loading">
        <div className="flex items-center gap-2 mb-6">
          <Crown className="w-8 h-8 text-yellow-400 animate-pulse" />
          <h1 className="text-xl font-bold">Loading Smart Money...</h1>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  const hotAlerts = data?.hot_alerts || [];
  const politicians = data?.politicians || [];
  const insiders = data?.insider_trades || [];
  const institutional = data?.institutional_buys || [];
  const famousInvestors = data?.famous_investors || [];
  const performance = data?.performance || {};

  return (
    <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-6" data-testid="smart-money-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold flex items-center gap-2">
            <Crown className="text-yellow-400 w-6 h-6 sm:w-8 sm:h-8" />
            Smart Money Tracker
          </h1>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">
            Follow the whales, politicians & insiders
          </p>
        </div>
        <Button 
          onClick={fetchData} 
          variant="outline" 
          size="sm"
          className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20 w-full sm:w-auto"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* 🤖 AUTOPILOT SECTION */}
      <Card className={`mb-4 ${autopilot?.settings?.enabled ? 'bg-gradient-to-br from-purple-900/50 to-gray-800 border-purple-500' : 'bg-gray-800 border-gray-700'}`} data-testid="autopilot-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base text-purple-400 flex items-center gap-2">
              <Target className="w-5 h-5" />
              🤖 AUTOPILOT - Copy Smart Money
            </CardTitle>
            <div className="flex items-center gap-2">
              {autopilot?.settings?.enabled && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-400"
                    onClick={() => setShowPerformance(!showPerformance)}
                  >
                    <BarChart3 className="w-3 h-3 mr-1" />
                    Stats
                    {showPerformance ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-500 text-purple-400"
                    onClick={syncAutopilot}
                    disabled={syncing}
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                    Sync
                  </Button>
                </>
              )}
              <Button
                size="sm"
                className={autopilot?.settings?.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}
                onClick={toggleAutopilot}
                disabled={autopilotLoading}
                data-testid="autopilot-toggle-btn"
              >
                {autopilotLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : autopilot?.settings?.enabled ? (
                  'Disable'
                ) : (
                  'Enable Autopilot'
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {autopilot?.settings?.enabled ? (
            <div>
              {/* Autopilot Quick Stats */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="text-center p-2 bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-400">Open</p>
                  <p className="text-lg font-bold text-purple-400" data-testid="autopilot-open-count">{performanceData?.summary?.open_positions || 0}</p>
                </div>
                <div className="text-center p-2 bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-400">Win Rate</p>
                  <p className="text-lg font-bold text-green-400" data-testid="autopilot-win-rate">{performanceData?.summary?.overall_win_rate || 0}%</p>
                </div>
                <div className="text-center p-2 bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-400">Unrealized</p>
                  <p className={`text-lg font-bold ${(performanceData?.summary?.total_unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="autopilot-unrealized-pnl">
                    {(performanceData?.summary?.total_unrealized_pnl || 0) >= 0 ? '+' : ''}{(performanceData?.summary?.total_unrealized_pnl || 0).toFixed(1)}%
                  </p>
                </div>
                <div className="text-center p-2 bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-lg font-bold text-blue-400" data-testid="autopilot-total-trades">{performanceData?.summary?.total_trades || 0}</p>
                </div>
              </div>
              
              {/* 📊 DETAILED PERFORMANCE ANALYTICS */}
              {showPerformance && performanceData && (
                <div className="space-y-4 mb-4 p-3 bg-gray-800/70 rounded-lg border border-purple-500/30" data-testid="autopilot-performance-panel">
                  {/* Summary Banner */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-gradient-to-br from-green-900/50 to-gray-800 p-3 rounded-lg text-center">
                      <p className="text-[10px] text-gray-400 uppercase">Realized P&L</p>
                      <p className={`text-xl font-bold ${(performanceData.summary?.total_realized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(performanceData.summary?.total_realized_pnl || 0) >= 0 ? '+' : ''}{performanceData.summary?.total_realized_pnl || 0}%
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-900/50 to-gray-800 p-3 rounded-lg text-center">
                      <p className="text-[10px] text-gray-400 uppercase">Unrealized P&L</p>
                      <p className={`text-xl font-bold ${(performanceData.summary?.total_unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(performanceData.summary?.total_unrealized_pnl || 0) >= 0 ? '+' : ''}{(performanceData.summary?.total_unrealized_pnl || 0).toFixed(2)}%
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-900/50 to-gray-800 p-3 rounded-lg text-center">
                      <p className="text-[10px] text-gray-400 uppercase">Combined P&L</p>
                      <p className={`text-xl font-bold ${(performanceData.summary?.combined_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(performanceData.summary?.combined_pnl || 0) >= 0 ? '+' : ''}{(performanceData.summary?.combined_pnl || 0).toFixed(2)}%
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-900/50 to-gray-800 p-3 rounded-lg text-center">
                      <p className="text-[10px] text-gray-400 uppercase">Closed Trades</p>
                      <p className="text-xl font-bold text-yellow-400">{performanceData.summary?.closed_trades || 0}</p>
                    </div>
                  </div>

                  {/* Performance by Source */}
                  <div>
                    <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> Performance by Source
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {/* Politicians */}
                      <div className="bg-blue-900/20 border border-blue-500/30 p-2 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🏛️</span>
                          <span className="text-sm font-semibold text-blue-400">Politicians</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div>
                            <span className="text-gray-500">Open:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.politician?.open_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Closed:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.politician?.closed_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Win Rate:</span>
                            <span className="text-green-400 ml-1">{performanceData.by_source?.politician?.win_rate || 0}%</span>
                          </div>
                          <div>
                            <span className="text-gray-500">P&L:</span>
                            <span className={`ml-1 ${(performanceData.by_source?.politician?.unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(performanceData.by_source?.politician?.unrealized_pnl || 0) >= 0 ? '+' : ''}{(performanceData.by_source?.politician?.unrealized_pnl || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Insiders */}
                      <div className="bg-orange-900/20 border border-orange-500/30 p-2 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🔒</span>
                          <span className="text-sm font-semibold text-orange-400">Insiders</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div>
                            <span className="text-gray-500">Open:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.insider?.open_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Closed:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.insider?.closed_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Win Rate:</span>
                            <span className="text-green-400 ml-1">{performanceData.by_source?.insider?.win_rate || 0}%</span>
                          </div>
                          <div>
                            <span className="text-gray-500">P&L:</span>
                            <span className={`ml-1 ${(performanceData.by_source?.insider?.unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(performanceData.by_source?.insider?.unrealized_pnl || 0) >= 0 ? '+' : ''}{(performanceData.by_source?.insider?.unrealized_pnl || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Fund Managers */}
                      <div className="bg-green-900/20 border border-green-500/30 p-2 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">💼</span>
                          <span className="text-sm font-semibold text-green-400">Fund Managers</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div>
                            <span className="text-gray-500">Open:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.fund_manager?.open_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Closed:</span>
                            <span className="text-white ml-1">{performanceData.by_source?.fund_manager?.closed_trades || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Win Rate:</span>
                            <span className="text-green-400 ml-1">{performanceData.by_source?.fund_manager?.win_rate || 0}%</span>
                          </div>
                          <div>
                            <span className="text-gray-500">P&L:</span>
                            <span className={`ml-1 ${(performanceData.by_source?.fund_manager?.unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(performanceData.by_source?.fund_manager?.unrealized_pnl || 0) >= 0 ? '+' : ''}{(performanceData.by_source?.fund_manager?.unrealized_pnl || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Performers */}
                  {performanceData.top_performers?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                        <Award className="w-3 h-3" /> Top Performers (by Win Rate)
                      </p>
                      <div className="space-y-1">
                        {performanceData.top_performers.slice(0, 5).map((performer, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-700/30 rounded text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-400 font-bold">#{idx + 1}</span>
                              <span className="text-white">{performer.name}</span>
                              <Badge className={performer.source === 'politician' ? 'bg-blue-600/50 text-[10px]' : performer.source === 'insider' ? 'bg-orange-600/50 text-[10px]' : 'bg-green-600/50 text-[10px]'}>
                                {performer.source}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-gray-400">{performer.trades} trades</span>
                              <span className="text-green-400 font-semibold">{performer.win_rate}% win</span>
                              <span className={`font-semibold ${performer.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {performer.avg_pnl >= 0 ? '+' : ''}{performer.avg_pnl}% avg
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Active Autopilot Trades */}
              {performanceData?.open_positions?.length > 0 && (
                <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="autopilot-positions">
                  <p className="text-xs text-gray-400 mb-2">📊 Active Positions ({performanceData.open_positions.length} copying smart money)</p>
                  {performanceData.open_positions.map((trade, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-700/30 rounded border border-purple-500/30" data-testid={`autopilot-position-${trade.symbol}`}>
                      <div className="flex items-center gap-2">
                        <Badge className={trade.source === 'politician' ? 'bg-blue-600' : trade.source === 'insider' ? 'bg-orange-600' : 'bg-green-600'}>
                          {trade.source === 'politician' ? '🏛️' : trade.source === 'insider' ? '🔒' : '💼'}
                        </Badge>
                        <div>
                          <p className="font-bold text-white">{trade.symbol}</p>
                          <p className="text-xs text-gray-400">{trade.source_name}</p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <p className="text-sm text-gray-300">${trade.entry_price?.toFixed(2)} → ${trade.current_price?.toFixed(2) || 'N/A'}</p>
                          <p className={`text-xs font-semibold ${(trade.pnl_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {(trade.pnl_pct || 0) >= 0 ? '+' : ''}{(trade.pnl_pct || 0).toFixed(2)}% (${(trade.pnl_dollar || 0).toFixed(0)})
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/20 h-7 px-2"
                          onClick={() => closeAutopilotTrade(trade.symbol)}
                          data-testid={`close-trade-${trade.symbol}`}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-400 text-sm mb-2">
                Automatically copy trades from politicians, insiders & fund managers
              </p>
              <p className="text-xs text-gray-500">
                Track: Nancy Pelosi, Warren Buffett, Michael Burry, Cathie Wood & more
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Summary */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
        <Card className="bg-gradient-to-br from-yellow-900/50 to-gray-800 border-yellow-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">Trades Tracked</p>
            <p className="text-lg sm:text-2xl font-bold text-yellow-400">{performance.total_tracked || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-900/50 to-gray-800 border-green-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">Total Profit</p>
            <p className="text-lg sm:text-2xl font-bold text-green-400">${performance.total_profit || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-900/50 to-gray-800 border-blue-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">Win Rate</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-400">{performance.win_rate || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Hot Alerts - Green Flashing */}
      {hotAlerts.length > 0 && (
        <Card className="bg-gray-800 border-2 border-green-500 mb-4 animate-pulse">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base text-green-400 flex items-center gap-2">
              <Zap className="w-5 h-5 animate-bounce" />
              🔥 HOT ALERTS - Take Action Now!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {hotAlerts.slice(0, 5).map((alert, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 bg-green-900/30 border border-green-600/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Badge className={alert.action?.includes("Purchase") || alert.action === "BUY" ? "bg-green-600" : "bg-red-600"}>
                    {alert.action?.includes("Purchase") || alert.action === "BUY" ? "BUY" : "SELL"}
                  </Badge>
                  <div>
                    <p className="font-bold text-white text-lg">{alert.symbol}</p>
                    <p className="text-xs text-gray-400">{alert.type} • {alert.source}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-green-400 max-w-[150px] truncate">{alert.reason}</p>
                  <Button
                    size="sm"
                    className="mt-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setSelectedAlert(alert);
                      setShowTradeDialog(true);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Take Trade
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-800 w-full grid grid-cols-4">
          <TabsTrigger value="alerts" className="text-xs sm:text-sm">
            <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            <span className="hidden sm:inline">Hot</span>
          </TabsTrigger>
          <TabsTrigger value="politicians" className="text-xs sm:text-sm">
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            <span className="hidden sm:inline">Politicians</span>
          </TabsTrigger>
          <TabsTrigger value="insiders" className="text-xs sm:text-sm">
            <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            <span className="hidden sm:inline">Insiders</span>
          </TabsTrigger>
          <TabsTrigger value="famous" className="text-xs sm:text-sm">
            <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            <span className="hidden sm:inline">Legends</span>
          </TabsTrigger>
        </TabsList>

        {/* Hot Alerts Tab */}
        <TabsContent value="alerts" className="space-y-3">
          {hotAlerts.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-400">No hot alerts right now</p>
                <p className="text-xs text-gray-500 mt-1">Check back later for actionable signals</p>
              </CardContent>
            </Card>
          ) : (
            hotAlerts.map((alert, idx) => (
              <Card key={idx} className="bg-gray-800 border-gray-700 hover:border-green-500 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        alert.action?.includes("Purchase") || alert.action === "BUY" 
                          ? "bg-green-500/20" 
                          : "bg-red-500/20"
                      }`}>
                        {alert.action?.includes("Purchase") || alert.action === "BUY" 
                          ? <TrendingUp className="w-6 h-6 text-green-500" />
                          : <TrendingDown className="w-6 h-6 text-red-500" />
                        }
                      </div>
                      <div>
                        <p className="font-bold text-white text-xl">{alert.symbol}</p>
                        <p className="text-sm text-gray-400">{alert.type}</p>
                        <p className="text-xs text-gray-500">{alert.source}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={alert.urgency === "HIGH" ? "bg-red-600" : "bg-yellow-600"}>
                        {alert.urgency}
                      </Badge>
                      <p className="text-xs text-gray-400 mt-2 max-w-[200px]">{alert.reason}</p>
                      <Button
                        size="sm"
                        className="mt-2 bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          setSelectedAlert(alert);
                          setShowTradeDialog(true);
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Log Trade
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Politicians Tab */}
        <TabsContent value="politicians" className="space-y-3">
          {politicians.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <Users className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                <p className="text-gray-400">No recent politician trades</p>
              </CardContent>
            </Card>
          ) : (
            politicians.map((trade, idx) => (
              <Card key={idx} className="bg-gray-800 border-gray-700">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={
                        trade.party === "Democrat" ? "bg-blue-600" : 
                        trade.party === "Republican" ? "bg-red-600" : "bg-gray-600"
                      }>
                        {trade.party?.charAt(0) || "?"}
                      </Badge>
                      <div>
                        <p className="font-bold text-white">{trade.symbol}</p>
                        <p className="text-xs text-gray-400">{trade.politician}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        trade.trade_type?.includes("Purchase") ? "bg-green-600" : "bg-red-600"
                      }>
                        {trade.trade_type?.includes("Purchase") ? "BUY" : "SELL"}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">{trade.amount}</p>
                      <p className="text-[10px] text-gray-600">{trade.date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Insiders Tab */}
        <TabsContent value="insiders" className="space-y-3">
          {insiders.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <Briefcase className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                <p className="text-gray-400">No recent insider trades</p>
              </CardContent>
            </Card>
          ) : (
            insiders.map((trade, idx) => (
              <Card key={idx} className={`bg-gray-800 border-gray-700 ${trade.is_hot ? 'border-green-500' : ''}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-white">{trade.symbol}</p>
                      <p className="text-xs text-gray-400">{trade.insider}</p>
                      <p className="text-[10px] text-gray-500">{trade.title}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        trade.trade_type?.includes("Purchase") || trade.trade_type?.includes("Buy") 
                          ? "bg-green-600" : "bg-red-600"
                      }>
                        {trade.trade_type?.includes("Purchase") || trade.trade_type?.includes("Buy") ? "BUY" : "SELL"}
                      </Badge>
                      <p className="text-sm font-bold text-white mt-1">
                        ${(trade.value || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-gray-500">{trade.shares?.toLocaleString()} shares</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Famous Investors Tab */}
        <TabsContent value="famous" className="space-y-3">
          <p className="text-sm text-gray-400 mb-2">
            Tracking portfolio moves of legendary investors (13F filings)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {famousInvestors.map((investor, idx) => (
              <Card key={idx} className="bg-gray-800 border-gray-700 hover:border-yellow-500 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Crown className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="font-bold text-white">{investor.name}</p>
                      <p className="text-xs text-gray-400">{investor.company}</p>
                      <Badge className="mt-1 bg-gray-700 text-gray-300 text-[10px]">
                        {investor.type}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Trade Dialog */}
      <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Log Smart Money Trade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-gray-800 p-3 rounded-lg">
              <p className="text-sm text-gray-400">Signal</p>
              <p className="font-bold text-white text-lg">{selectedAlert?.symbol}</p>
              <p className="text-xs text-gray-500">{selectedAlert?.type} • {selectedAlert?.source}</p>
              <p className="text-xs text-green-400 mt-1">{selectedAlert?.reason}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Shares</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={tradeForm.shares}
                  onChange={(e) => setTradeForm({...tradeForm, shares: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Entry Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="150.00"
                  value={tradeForm.entry_price}
                  onChange={(e) => setTradeForm({...tradeForm, entry_price: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
            </div>
            
            <Button onClick={handleTakeTrade} className="w-full bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Log This Trade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer with Cache Info */}
      <div className="mt-6 text-center text-gray-500 text-xs space-y-1">
        <p>Data sources: Unusual Whales, SEC Filings, 13F Reports</p>
        {data?.cache_info && (
          <div className="text-gray-600">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
              data.cache_info.market_status === "OPEN" 
                ? "bg-green-500/20 text-green-400" 
                : "bg-gray-700 text-gray-400"
            }`}>
              {data.cache_info.market_status === "OPEN" ? "🟢" : "🔴"} Market {data.cache_info.market_status}
            </span>
            <span className="ml-2">
              Refresh: {data.cache_info.refresh_interval}
            </span>
            {data.cache_info.congress_cached_at && (
              <span className="ml-2">
                • Last: {new Date(data.cache_info.congress_cached_at).toLocaleTimeString()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
